import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../api/ApiProvider";
import { publicHolidayKeys } from "./publicHoliday.keys";
import { CreatePublicHolidayDto, UpdatePublicHolidayDto } from "../../api-client";
import notificationStore from "../Notification/notificationStore";

/**
 * Hook to create a single public holiday
 */
export const useCreatePublicHoliday = () => {
  const { publicHoliday } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreatePublicHolidayDto) => publicHoliday.createPublicHoliday(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: publicHolidayKeys.publicHolidays.all() });
      notificationStore.showMessage("Feiertag erfolgreich erstellt", "success");
    },
    onError: (error) => {
      notificationStore.showMessage("Fehler beim Erstellen des Feiertags", "error", error);
    },
  });
};

/**
 * Hook to create public holidays in bulk
 */
export const useCreatePublicHolidaysBulk = () => {
  const { publicHoliday } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dtos: CreatePublicHolidayDto[]) => publicHoliday.createPublicHolidaysBulk(dtos),
    onSuccess: (_, variables) => {
      // Invalidate queries for the years we just added
      const years = [...new Set(variables.map(dto => dto.year))];
      years.forEach(year => {
        queryClient.invalidateQueries({ 
          queryKey: publicHolidayKeys.publicHolidays.byYear(year) 
        });
      });
      queryClient.invalidateQueries({ queryKey: publicHolidayKeys.publicHolidays.all() });
      notificationStore.showMessage(
        `${variables.length} Feiertag(e) erfolgreich importiert`, 
        "success"
      );
    },
    onError: (error) => {
      notificationStore.showMessage("Fehler beim Importieren der Feiertage", "error", error);
    },
  });
};

/**
 * Hook to update a public holiday
 */
export const useUpdatePublicHoliday = () => {
  const { publicHoliday } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdatePublicHolidayDto }) =>
      publicHoliday.updatePublicHoliday(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: publicHolidayKeys.publicHolidays.all() });
      notificationStore.showMessage("Feiertag erfolgreich aktualisiert", "success");
    },
    onError: (error) => {
      notificationStore.showMessage("Fehler beim Aktualisieren des Feiertags", "error", error);
    },
  });
};

/**
 * Hook to delete a single public holiday
 */
export const useDeletePublicHoliday = () => {
  const { publicHoliday } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => publicHoliday.deletePublicHoliday(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: publicHolidayKeys.publicHolidays.all() });
      notificationStore.showMessage("Feiertag erfolgreich gelöscht", "success");
    },
    onError: (error) => {
      notificationStore.showMessage("Fehler beim Löschen des Feiertags", "error", error);
    },
  });
};

/**
 * Hook to delete all public holidays for a year (optionally filtered by bundesland)
 */
export const useDeletePublicHolidaysByYear = () => {
  const { publicHoliday } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ year, bundesland }: { year: number; bundesland?: string }) =>
      publicHoliday.deletePublicHolidaysByYear(year, bundesland),
    onSuccess: (_, { year, bundesland }) => {
      queryClient.invalidateQueries({ 
        queryKey: publicHolidayKeys.publicHolidays.byYear(year) 
      });
      queryClient.invalidateQueries({ queryKey: publicHolidayKeys.publicHolidays.all() });
      const msg = bundesland
        ? `Alle Feiertage für ${bundesland} in ${year} gelöscht`
        : `Alle Feiertage für ${year} gelöscht`;
      notificationStore.showMessage(msg, "success");
    },
    onError: (error) => {
      notificationStore.showMessage("Fehler beim Löschen der Feiertage", "error", error);
    },
  });
};